import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { LogoLockup } from "./Logo";

const links = [
  { label: "Products", href: "#products" },
  { label: "The Alliance", href: "#about" },
  { label: "Answers", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const prefix = pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-navy/80 border-b border-steel/30"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <a href={prefix ? "/" : "#top"} data-testid="nav-logo-link" aria-label="Northwest Tech Alliance home">
          <LogoLockup compact />
        </a>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10" data-testid="nav-links">
          {links.map((l) => (
            <a
              key={l.label}
              href={`${prefix}${l.href}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono-accent text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-tech-bright transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href={`${prefix}#contact`}
            data-testid="nav-cta-button"
            className="group relative font-mono-accent text-[11px] tracking-[0.22em] uppercase px-5 py-2.5 border border-tech text-fog overflow-hidden"
          >
            <span className="absolute inset-0 bg-tech translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
            <span className="relative">Talk to Sales</span>
          </a>
        </nav>

        <button
          className="lg:hidden text-fog"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-navy-deep/95 backdrop-blur-xl border-t border-steel/30 px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a
              key={l.label}
              href={`${prefix}${l.href}`}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono-accent text-xs tracking-[0.22em] uppercase text-fog/80"
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </motion.header>
  );
};
